import type { CompetitorMemberRef } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DetailFailure, DetailHeader, DetailSkeleton, OUTLINE_LINK } from "../../components/nocs/detail-parts";
import { NocBadge } from "../../components/nocs/noc-badge";
import { ShellCrumbs } from "../../components/shell/crumbs";
import { EntryActions } from "../../components/teams/entry-actions";
import { EntryStatusChip } from "../../components/teams/entry-status";
import { EventMeets } from "../../components/teams/event-meets";
import { entryMeetCount, entryMeetGroups, eventTrail, startListMeet } from "../../components/teams/labels";
import { competitorDetailQuery } from "../../components/teams/queries";
import { RosterTable } from "../../components/teams/roster-table";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/page/card";
import { errorMessage } from "../../lib/api/messages";
import { useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";
import { eventStructureQuery } from "../../lib/meet";

const isUuid = (v: string) => z.string().uuid().safeParse(v).success;

/**
 * One entry (`GET /competitors/:id`) with its event and the event's meets
 * (`GET /events/:id/structure` — one read, every phase and meet): who is on
 * the roster, and where this entry plays. A write role manages the entry
 * itself here — seed, entry mark, withdraw / reinstate, delete while it
 * plays nowhere (`EntryActions`); its roster is changed in a meet's start
 * list. Who is entered (registration, sport, shortlist) is the GMS's.
 *
 * An entry belongs to one games; a link from another games moves the
 * sidebar to it, as other links do.
 */
export const Route = createFileRoute("/_app/teams/$competitorId")({
	component: EntryPage,
});

function EntryPage() {
	const { t } = useTranslation("teams");
	const { competitorId } = Route.useParams();
	const { readOnly } = Route.useRouteContext();
	const navigate = useNavigate();
	const current = useCurrentGames();
	const valid = isUuid(competitorId);
	const id = competitorId.toLowerCase();

	const entry = useQuery({ ...competitorDetailQuery(id), enabled: valid });
	const structure = useQuery({ ...eventStructureQuery(entry.data?.event_id ?? ""), enabled: entry.data !== undefined });
	const event = structure.data?.event;
	const groups = useMemo(() => (structure.data ? entryMeetGroups(structure.data, id) : []), [structure.data, id]);

	// The entry's games is not the sidebar's: the sidebar follows the link (once per games).
	const entryGames = event?.games.games_id;
	useEffect(() => {
		if (entryGames && current.gamesId !== null && current.gamesId !== entryGames) current.setGamesId(entryGames);
	}, [entryGames]);

	const crumbs = <ShellCrumbs items={[{ label: t("title"), to: { to: "/teams" } }, { label: entry.data?.name ?? t("detailTitle") }]} />;

	if (!valid || entry.isError) {
		return (
			<div className="flex flex-col gap-[1rem]">
				{crumbs}
				<h1 className="font-black text-[1.5rem] text-ink">{t("detailTitle")}</h1>
				<DetailFailure
					error={valid ? entry.error : "not-found"}
					notFound={{
						title: t("detail.notFoundTitle"),
						body: t("detail.notFoundBody"),
						action: (
							<Link to="/teams" className={OUTLINE_LINK}>
								{t("detail.backToList")}
							</Link>
						),
					}}
					onRetry={() => void entry.refetch()}
				/>
			</div>
		);
	}

	const c = entry.data;
	if (!c) {
		return (
			<div className="flex flex-col">
				{crumbs}
				<DetailSkeleton label={t("detail.loading")} />
			</div>
		);
	}

	const openMember = (m: CompetitorMemberRef) =>
		void navigate({ to: "/athletes/$personId", params: { personId: m.people_id }, search: { games: entryGames ?? current.gamesId ?? undefined } });
	const editMeet = startListMeet(groups);

	const facts = [
		{ key: "status", label: t("detail.facts.status"), value: <EntryStatusChip status={c.status} /> },
		...(event ? [{ key: "type", label: t("detail.facts.type"), value: event.competitor_type === "TEAM" ? t("detail.team") : t("detail.individual") }] : []),
		{ key: "entryNo", label: t("detail.facts.entryNo"), value: <span className="font-mono">{formatInteger(c.entry_no)}</span> },
		...(c.seed !== null ? [{ key: "seed", label: t("detail.facts.seed"), value: <span className="font-mono">{formatInteger(c.seed)}</span> }] : []),
		...(c.entry_mark ? [{ key: "entryMark", label: t("detail.facts.entryMark"), value: <span className="font-mono">{c.entry_mark}</span> }] : []),
		...(c.bib ? [{ key: "bib", label: t("detail.facts.bib"), value: <span className="font-mono">{c.bib}</span> }] : []),
	];

	return (
		<div className="flex flex-col">
			{crumbs}
			<DetailHeader
				kicker={
					<>
						<NocBadge noc={c.noc} withName />
						{event && (
							<span className="flex min-w-0 flex-wrap items-center gap-x-[0.25rem]">
								{eventTrail(event).map((part) => (
									<span key={part}>
										{part}
										<span aria-hidden="true" className="px-[0.25rem]">
											›
										</span>
									</span>
								))}
								<Link
									to="/sports/$sportId"
									params={{ sportId: event.sport.sport_id }}
									search={{ discipline: event.discipline.discipline_id }}
									className="font-bold text-list hover:underline"
								>
									{event.name}
								</Link>
							</span>
						)}
					</>
				}
				title={c.name}
				facts={facts}
				actions={
					readOnly ? undefined : (
						<EntryActions
							entry={c}
							eventId={c.event_id}
							meetCount={structure.data ? entryMeetCount(groups) : null}
							onDeleted={() => void navigate({ to: "/teams" })}
						/>
					)
				}
			/>

			<div className="flex flex-col gap-[1rem]">
				<RosterTable
					members={c.members}
					onOpen={openMember}
					footer={
						readOnly ? undefined : editMeet ? (
							<span className="flex flex-wrap items-center gap-x-[0.5rem] gap-y-[0.25rem]">
								{t("detail.roster.readOnly")}
								<Link to="/meets/$meetId/start-list" params={{ meetId: editMeet.meet_id }} className="font-bold text-list hover:underline">
									{t("detail.meets.openStartList", { meet: editMeet.meet_no })}
								</Link>
							</span>
						) : structure.data ? (
							t("detail.roster.readOnlyNoMeet")
						) : undefined
					}
				/>

				{structure.isError ? (
					<Card className="flex flex-col items-start gap-[0.5rem] px-[1rem] py-[1rem]">
						<p className="font-bold text-ink">{t("detail.structureError")}</p>
						<p className="text-[0.875rem] text-muted">{errorMessage(structure.error)}</p>
						<RetryButton onRetry={() => void structure.refetch()} />
					</Card>
				) : structure.data ? (
					<EventMeets groups={groups} />
				) : (
					<Card className="px-[1rem] py-[1rem]">
						<div className="skeleton h-[1rem] w-[60%] rounded-[0.25rem]" />
					</Card>
				)}
			</div>
		</div>
	);
}

function RetryButton({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<Button variant="outline" size="sm" onClick={onRetry}>
			{t("actions.retry")}
		</Button>
	);
}
